import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import Transition from 'v2/components/Shared/Transition';
import ordersService from 'v2/services/orders';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import type { ERefundMotiveId } from 'v2/interfaces/requestManagement/refund';
import type { ISendRefund } from 'v2/interfaces/requestManagement/refund';

import { validationSchema } from './validation';

import type { FormikProps } from 'formik';

interface IFormikValues {
  motive_id: ERefundMotiveId;
  motive: string;
}

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
  },
}));

export function ModalRefund() {
  const classes = useStyles();

  const [open] = useObservable(() => ordersService.getModalRefund(), []);
  const [invoice] = useObservable(() => ordersService.getInvoice(), []);
  const onClose = useCallback(() => ordersService.closeModalRefund(), []);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const onHandleClose = useCallback(
    (resetForm) => {
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [onSubmit] = useCallbackObservable(
    (values: IFormikValues, { setSubmitting, resetForm }: FormikProps<IFormikValues>) => {
      setSubmitting(true);

      let { description } = motives.find((motive) => motive.id === values.motive_id) || {};

      const formattedValues: ISendRefund = {
        motive_id: values.motive_id,
        invoice_id: invoice.id,
        motive: description,
      };

      return refundRequestFactory.sendRefundRequest(formattedValues).pipe(
        tap(() => {
          setSubmitting(false);
          onHandleClose(resetForm);
          toast.success('Solicitação realizada com sucesso!');
        }),
        catchError((err) => {
          setSubmitting(false);
          console.error(err);
          toast.error('Não foi possível reembolsar a fatura.');
          return of(true);
        })
      );
    },
    [motives, invoice]
  );

  const motiveOptions = (motives || []).map((item) => ({
    label: item.description,
    value: item.id,
  }));

  return (
    <Formik validationSchema={validationSchema} initialValues={{ motive_id: '', motive: '' }} onSubmit={onSubmit}>
      {({ isSubmitting, submitForm, resetForm, isValid, dirty }) => {
        const shouldDisableConfirmBtn = !isValid || isSubmitting || !dirty;

        return (
          <Form>
            <Dialog
              id='orders-modal-refund'
              TransitionComponent={Transition}
              open={open}
              onClose={() => onHandleClose(resetForm)}
              fullScreen={fullScreen}
              fullWidth={true}
              disableRestoreFocus
            >
              <DialogTitle id='orders-modal-refund-title' onClose={() => onHandleClose(resetForm)}>
                <Typography variant='h6'>Deseja realmente reembolsar essa fatura?</Typography>
              </DialogTitle>
              <DialogContent dividers>
                {loading || isSubmitting ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin />
                  </div>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field id='motive_id' name='motive_id' options={motiveOptions} component={ListRadioButtonFormField} />
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button color='default' onClick={() => onHandleClose(resetForm)}>
                  Cancelar
                </Button>
                <Button onClick={submitForm} type='submit' variant='contained' color='secondary' disabled={shouldDisableConfirmBtn}>
                  Confirmar
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
}
